/**
 * The product list — `screens.md` §3, on `components.md`'s dense table.
 *
 * Nine hundred and twelve SKUs is the design's own worked example and it is the number
 * every decision here is made against: the rows are windowed because nine hundred DOM rows
 * is a slow screen, the count comes from the server because a client cannot count what it
 * has not loaded, and the search marks its hit because a fourteen-digit number with a
 * four-digit match in the middle of it is unreadable otherwise.
 *
 * ## Two states this screen does not build, and why that is the design's own rule
 *
 * §3 lists eleven states. Two have **no subject in Phase 2** (F7, enumerated in the Block
 * D re-plan so they would not be met one at a time):
 *
 *   - **Bulk publish over limit with exact math.** No entitlement gate exists anywhere in
 *     Phase 2 — measured, not assumed — so "over limit" is a state nothing can enter. The
 *     arithmetic would be invented, and `acceptance-tests.md`'s governing line is *"nothing
 *     invented; open questions flagged rather than filled in."*
 *   - **Background artwork job with progress.** Artwork is Phase 3's. A progress bar for a
 *     job that cannot be started is a remedy that does not exist (c29's rule).
 * The empty state's import door was a third, deferred until the import route existed. It
 * leads the empty state since U78 — Drift Log c34.
 *
 * ## What holds the state, and what decides
 *
 * This component holds the state; the *decisions* are in pure modules — `packages/ui`'s
 * `window.ts`, `selection.ts`, `keyboard.ts`, `match.ts`, and this directory's
 * `product-row.ts` and `search-cause.ts`. Neither this package nor `packages/ui` runs
 * jsdom, so anything expressed as a handler is unreachable by this repo's suites; anything
 * expressed as a function is one call away. The real scrolling and the real keystrokes are
 * c38's Playwright work, which is the only place they can honestly be exercised.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";

import {
  Button,
  DataTable,
  EMPTY_SELECTION,
  INITIAL_CURSOR,
  Marked,
  Pill,
  Skeleton,
  Surface,
  TABLE_ROW_HEIGHT,
  TableSelectionBar,
  clearSelection,
  isRowSelected,
  rowWindow,
  selectWholeFilter,
  selectionSize,
  toggleRow,
  type Column,
  type DataTableRow,
  type TableCursor,
  type TableSelection,
} from "@packagepage/ui";
import {
  PRODUCT_SORT_COLUMNS,
  PRODUCT_STATUSES,
  type ProductSortColumn,
  type ProductStatus,
} from "@packagepage/shared";

import { problemOf } from "../api/client.js";
import { api } from "../api/procedures.js";
import {
  STATUS_LABEL,
  STATUS_PILL,
  displayName,
  ineligibleReason,
  publishSplit,
} from "./product-row.js";
import { validateProductSearch } from "./product-search.js";
import { zeroResultCause } from "./search-cause.js";

// Imported here rather than relied on from the shell. `packages/ui` exports one stylesheet
// per component and never aggregates them, and the rule that goes with that is *consumers
// import the stylesheets they use* — a screen that renders correctly only because a parent
// happened to import its CSS breaks the day the parent stops needing it.
import "@packagepage/ui/table.css";
import "@packagepage/ui/skeleton.css";
import "@packagepage/ui/button.css";
import "@packagepage/ui/pill.css";
import "./product-list.css";

/**
 * How tall the scrolling body is, in rows.
 *
 * Expressed in rows and multiplied out, rather than written as a pixel count, so it stays
 * a whole number of rows as the row height is the one geometry constant. A viewport that
 * ends mid-row is not wrong — `rowWindow` covers the partial row — but a half row at the
 * bottom of a dense table reads as content that failed to load.
 */
const VIEWPORT_ROWS = 14;
const VIEWPORT_HEIGHT = VIEWPORT_ROWS * TABLE_ROW_HEIGHT;

/**
 * How close to the end of the loaded rows a scroll gets before the next page is asked for.
 *
 * One overscan's worth plus a little: the fetch has to finish before the window reaches the
 * end, or the user sees the bottom of the list and a pause. Two hundred rows would be
 * smoother and would also mean a screen that scrolls three rows fetches nine hundred.
 */
const PREFETCH_MARGIN_ROWS = 20;

/** `components.md`: *"Search — debounced 200ms across name, SKU, GTIN."* Lot since U52. */
export const SEARCH_DEBOUNCE_MS = 200;

interface Row extends DataTableRow {
  readonly id: string;
  readonly gtin14: string;
  readonly sku: string | null;
  readonly name: unknown;
  readonly status: ProductStatus;
  readonly published_at: string | null;
  readonly created_at: string;
  readonly matched_lot?: string | null;
}

/**
 * The date column, in the fixed form the product uses everywhere.
 *
 * `screens.md` §4 fixes `YYYY-MM-DD` for expiry and the same form is used here: it sorts as
 * text, it is unambiguous between an American and a European reader, and it is the one date
 * format that does not change meaning when a brand in Hamburg reads a screen written in
 * London.
 */
function isoDate(value: string | null): string {
  return value === null ? "—" : value.slice(0, 10);
}

/** `LOT42 · lot` beside the name, when `?q=` matched a batch exactly — plan 3R §4 U52. */
export function MatchedLot({ lot, query }: { lot: string | null; query: string }) {
  if (lot === null) return null;
  return (
    <span className="pp-products__lot" data-matched-lot={lot}>
      {" "}
      <Marked text={lot} query={query} mono /> · lot
    </span>
  );
}

export function ProductList(): ReactNode {
  const queryClient = useQueryClient();
  const searchRef = useRef<HTMLInputElement>(null);

  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  // The URL holds the status, so *Review 1,240 drafts* can arrive filtered and the nav's
  // Products link clears it. `strict: false` because this component also renders off-route.
  const status: ProductStatus | "" =
    validateProductSearch(useSearch({ strict: false })).status ?? "";
  const setStatus = (next: ProductStatus | "") =>
    void navigate({
      to: "/products",
      search: next === "" ? {} : { status: next },
      replace: true,
    });
  /**
   * The sort, typed as the **shared enum** rather than as a string.
   *
   * `DataTable`'s `sortKey` is a `string` because the table knows nothing about products,
   * so the narrowing has to happen here — and it happens by *validating* the header's key
   * against `PRODUCT_SORT_COLUMNS` rather than by asserting it. A cast would compile and
   * would send an unknown column to an endpoint that refuses it, which is a screen that
   * stops loading because of a rename nobody noticed.
   */
  const [sort, setSort] = useState<{ key: ProductSortColumn; order: "asc" | "desc" }>({
    key: "createdAt",
    order: "desc",
  });

  const [scrollTop, setScrollTop] = useState(0);
  const [selection, setSelection] = useState<TableSelection>(EMPTY_SELECTION);
  const [cursor, setCursor] = useState<TableCursor>(INITIAL_CURSOR);
  const [publishReport, setPublishReport] = useState<string | null>(null);

  // Debounced, and the timer is cleared on every keystroke so the request is made once the
  // typing stops rather than once per character. The state is split in two — `typed` is
  // what the input shows, `q` is what the server is asked — because a single value would
  // either lag the cursor or fire on every letter.
  useEffect(() => {
    const timer = setTimeout(() => setQ(typed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  // `\u0000` as escapes, not as raw NUL bytes. Written raw, the three separators made
  // git classify this file as **binary** (`git ls-files --eol` reported `i/-text`), and a
  // binary file is skipped by every ripgrep directory scan — so this screen was invisible
  // to `rg`/Grep while being perfectly ordinary TypeScript. Found at c38 when a search for
  // `DataTable` under apps/app/src returned two files and this one, which contains five
  // matches, was not among them.
  //
  // The separator itself is right and is kept: `q` is free text and can contain any
  // printable character, so a printable separator could be typed by a user and collide.
  // The escape produces the identical string at runtime and leaves the file readable by
  // the tools that read this repository.
  const filterKey = `${q}\u0000${status}\u0000${sort.key}\u0000${sort.order}`;

  /**
   * The selection is discarded whenever the filter changes, and it has to be.
   *
   * A filter selection means *"everything the current filter matches"* — it names no ids at
   * all, which is what lets it mean nine hundred rows while a hundred are loaded. Change
   * the query and the same value now means a different set, silently. `selection.ts` states
   * the rule and cannot enforce it: it never sees the filter. This is where it lives.
   */
  useEffect(() => {
    setSelection(EMPTY_SELECTION);
    setCursor(INITIAL_CURSOR);
    setScrollTop(0);
    setPublishReport(null);
  }, [filterKey]);

  const list = useInfiniteQuery({
    queryKey: ["products", "list", filterKey],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.products({
        ...(q === "" ? {} : { q }),
        ...(status === "" ? {} : { status }),
        ...(pageParam === undefined ? {} : { cursor: pageParam }),
        sort: sort.key,
        order: sort.order,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const rows: Row[] = useMemo(
    () =>
      (list.data?.pages ?? []).flatMap((page) =>
        page.products.map((p): Row => {
          // The reason a row cannot be selected travels **with the row**, so the table
          // renders it in the primary cell without knowing what a product is. Absent
          // rather than `undefined`, because `exactOptionalPropertyTypes` treats those as
          // different things and the table's contract is "present means unselectable".
          const reason = ineligibleReason(p.status);
          return reason === undefined ? { ...p } : { ...p, ineligible: reason };
        }),
      ),
    [list.data],
  );

  /**
   * The filter count, from the server.
   *
   * Read off the **first** page, which is the only one that answers the question: every
   * page carries the same count (it is a subquery over the filters alone, deliberately not
   * over the keyset), and page zero is the one that exists before any scrolling. `null`
   * while it is unknown — never `rows.length`, which is the number this whole arrangement
   * exists to stop being mistaken for the total.
   */
  const total = list.data?.pages[0]?.total ?? null;

  const selected = useCallback((id: string) => isRowSelected(selection, id), [selection]);
  const selectedCount = selectionSize(selection, total);
  const split = useMemo(() => publishSplit(rows, selected), [rows, selected]);

  const publish = useMutation({
    mutationFn: async (ids: readonly string[]) => {
      // One call per row, sequentially: there is no bulk endpoint behind this screen (§5
      // specifies `PATCH /products:batch` and nothing has built it), and publishing in
      // parallel would race a hundred outbox writes against each other for no gain a user
      // can see. Failures are collected rather than thrown, because `screens.md` §7's
      // idiom for a long apply is *"per-row failures reported as they happen"* — one bad
      // row must not abandon the ninety-nine behind it.
      const failures: { id: string; detail: string }[] = [];
      let published = 0;
      for (const id of ids) {
        try {
          await api.publishProduct(id);
          published += 1;
        } catch (error) {
          const problem = problemOf(error);
          failures.push({
            id,
            detail: problem?.detail ?? "That product could not be published.",
          });
        }
      }
      return { published, failures };
    },
    onSuccess: (result) => {
      setPublishReport(
        result.failures.length === 0
          ? `${result.published} ${result.published === 1 ? "product is" : "products are"} published — their codes started answering within seconds.`
          : `${result.published} published. ${result.failures.length} could not be: ${result.failures[0]?.detail ?? ""}`,
      );
      setSelection(clearSelection());
      void queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });

  const onScroll = useCallback(
    (next: number) => {
      setScrollTop(next);
      const slice = rowWindow(next, VIEWPORT_HEIGHT, rows.length);
      if (
        slice.end >= rows.length - PREFETCH_MARGIN_ROWS &&
        list.hasNextPage &&
        !list.isFetchingNextPage
      ) {
        void list.fetchNextPage();
      }
    },
    [rows.length, list],
  );

  const columns: ReadonlyArray<Column<Row>> = useMemo(
    () => [
      {
        key: "name",
        header: "Product",
        width: 340,
        primary: true,
        sortKey: "name",
        render: (row) => {
          const name = displayName(row.name, row.gtin14);
          return (
            <Link
              to="/products/$productId"
              params={{ productId: row.id }}
              className="pp-products__name"
            >
              <Marked text={name.text} query={q} />
              {name.lang === null ? null : (
                <span className="pp-products__lang"> · {name.lang}</span>
              )}
              <MatchedLot lot={row.matched_lot ?? null} query={q} />
            </Link>
          );
        },
      },
      {
        key: "gtin14",
        header: "Code",
        width: 190,
        foldsNarrow: true,
        sortKey: "gtin14",
        render: (row) => <Marked text={row.gtin14} query={q} mono />,
      },
      {
        key: "sku",
        header: "SKU",
        width: 140,
        foldsNarrow: true,
        render: (row) =>
          row.sku === null ? "—" : <Marked text={row.sku} query={q} mono />,
      },
      {
        key: "status",
        header: "Status",
        width: 150,
        sortKey: "status",
        render: (row) => (
          <Pill family="product" value={STATUS_PILL[row.status]}>
            {STATUS_LABEL[row.status]}
          </Pill>
        ),
      },
      /**
       * **This column named three different fields**, and now names one.
       *
       * It read `Published`, rendered `published_at`, and sorted `createdAt`. Each leg
       * was defensible alone; together they meant a user clicking a header labelled
       * "Published" reordered the list by creation date and watched a column of publish
       * dates fail to sort. Renaming the header alone would only have moved the lie from
       * header-versus-sort to header-versus-cells.
       *
       * **It follows the sort rather than the other way round**, and the reason is
       * `published_at`'s nullability. `PRODUCT_SORT_COLUMNS` carries `createdAt` and no
       * publish date, and adding one is not a rename: `product-list.ts`'s keyset compares
       * `${sortExpr}::text`, a NULL member silently **drops rows** from the comparison,
       * and every draft has a null publish date — so it needs a coalesce sentinel, a NULLS
       * placement, and an answer to *"where do drafts sit in a published-date ordering"*,
       * which is a product decision rather than an implementation detail.
       *
       * **It is also interim, and the handoff is the reason to say so.** `Screen · Product
       * List.dc.html`'s header row is `PRODUCT · GTIN · STATUS · LINKS · UPDATED ↓` — the
       * design has no Published column at all, and its date column is `UPDATED`, whose
       * `updated_at` is on neither the wire nor the sort enum. The column set is a known
       * divergence with its own round; the fork above belongs there, decided once against
       * the mock, rather than half-answered here.
       */
      {
        key: "added",
        header: "Added",
        width: 130,
        foldsNarrow: true,
        sortKey: "createdAt",
        render: (row) => isoDate(row.created_at),
        renderFolded: (row) => isoDate(row.created_at),
      },
      // A link, not an overflow menu: one action does not earn a menu component (plan §9 Q7).
      {
        key: "actions",
        header: "Actions",
        width: 200,
        render: (row) => (
          <Link
            to="/recalls/new"
            search={{ product: row.id }}
            aria-label={`Publish a recall notice for ${displayName(row.name, row.gtin14).text}`}
          >
            Publish a recall notice
          </Link>
        ),
      },
    ],
    [q],
  );

  const onSort = useCallback((key: string) => {
    // Validated, not asserted — see the state's declaration. An unknown key is ignored
    // rather than sent: a column whose `sortKey` no longer matches the enum is a bug in
    // this file's column model, and answering it by refusing to sort is quieter than
    // answering it with a list that stops loading.
    if (!PRODUCT_SORT_COLUMNS.includes(key as ProductSortColumn)) return;
    const column = key as ProductSortColumn;
    // Clicking the sorted column reverses it; clicking another moves to it, descending,
    // because every column here answers "most recent / highest first" on its first press.
    setSort((current) =>
      current.key === column
        ? { key: column, order: current.order === "asc" ? "desc" : "asc" }
        : { key: column, order: "desc" },
    );
  }, []);

  if (list.isPending) {
    return (
      <Surface>
        <div data-screen-label="Product list" className="pp-products__loading">
          {/* The skeleton matches the real geometry exactly — same row height, same
              column count — so nothing jumps when the rows arrive. No shimmer: at nine
              hundred rows a shimmer sweep is a strobe. */}
          {Array.from({ length: VIEWPORT_ROWS }, (_, i) => (
            <div
              key={i}
              className="pp-products__skeleton-row"
              style={{ height: `${TABLE_ROW_HEIGHT}px` }}
            >
              <Skeleton width="60%" />
              <Skeleton width="14ch" />
              <Skeleton width="8ch" />
            </div>
          ))}
        </div>
      </Surface>
    );
  }

  if (list.error !== null) {
    const problem = problemOf(list.error);
    return (
      <Surface tone="error" emphasis="filled">
        <div data-screen-label="Product list">
          <strong>
            {problem === null ? "We could not load your catalogue" : problem.title}
          </strong>
          <p>
            {problem === null
              ? "Your printed codes are unaffected — the resolver serves them from its own mirror and does not depend on this screen. Reload to try again."
              : problem.detail}
          </p>
        </div>
      </Surface>
    );
  }

  const filtering = q !== "" || status !== "";

  return (
    <div data-screen-label="Product list" className="pp-products">
      <div className="pp-products__controls">
        <input
          ref={searchRef}
          type="search"
          className="pp-products__search"
          value={typed}
          placeholder="Search names, SKUs, codes and lots"
          aria-label="Search names, SKUs, codes and lots"
          onChange={(e) => setTyped(e.target.value)}
        />
        <select
          className="pp-products__status"
          value={status}
          aria-label="Filter by status"
          onChange={(e) => setStatus(e.target.value as ProductStatus | "")}
        >
          <option value="">Every status</option>
          {PRODUCT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <span className="pp-products__count">
          {total === null
            ? "Counting…"
            : `${total} ${total === 1 ? "product" : "products"}${filtering ? " match" : ""}`}
        </span>
      </div>

      {publishReport === null ? null : (
        <Surface tone="neutral" emphasis="filled">
          <p role="status">{publishReport}</p>
        </Surface>
      )}

      {rows.length === 0 ? (
        filtering ? (
          <ZeroResults query={q} status={status} />
        ) : (
          <EmptyCatalogue />
        )
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          caption="Products"
          selection={selection}
          onToggle={(id) => setSelection((s) => toggleRow(s, id))}
          onSelectAll={(checked) =>
            setSelection(checked ? selectWholeFilter() : clearSelection())
          }
          allSelected={selection.mode === "filter" && selection.excluded.size === 0}
          sort={sort as { key: string; order: "asc" | "desc" }}
          onSort={onSort}
          scrollTop={scrollTop}
          viewportHeight={VIEWPORT_HEIGHT}
          onScroll={onScroll}
          cursor={cursor}
          onCursorChange={setCursor}
          /**
           * `Space`, `Escape` and `Shift+↑↓` land here — and until this line they landed
           * nowhere.
           *
           * `TableCursor` used to carry its own `selection`, so those three bindings
           * returned a cursor with a changed selection, `setCursor` stored it, and the
           * table went on rendering `selection` — the state below. Two sources of truth,
           * and the rendered one was never the one the keyboard wrote to. Three of the six
           * bindings `components.md` specifies were dead, with every suite green: the
           * model's tests asserted its return value and were right about it.
           *
           * There is one selection now, and this is the line that says so.
           */
          onSelectionChange={setSelection}
          /**
           * `Enter` opens the row the cursor is on — and it did nothing until c37c.
           *
           * This table had the mirror of the audit log's defect: it passes `cursor` and
           * renders a real `<Link>` in the primary column, so the **mouse** path was
           * healthy, and it passed no `onOpen`, so `tableKeyAction` returned an open the
           * grid then dropped. `components.md`'s binding table and `acceptance-tests.md`
           * both specify `Enter open`, and it was dead.
           *
           * That direction cannot be a type error — a table with no open concept legally
           * takes `cursor` without `onOpen` — which is why the two screens shipped missing
           * different halves of one contract and nothing mechanical saw either.
           */
          onOpen={(id) =>
            void navigate({ to: "/products/$productId", params: { productId: id } })
          }
          onFocusSearch={() => searchRef.current?.focus()}
          selectionBar={
            selectedCount === 0 ? null : (
              <TableSelectionBar count={selectedCount}>
                <BulkActions
                  selectedCount={selectedCount}
                  split={split}
                  running={publish.isPending}
                  onPublish={() => publish.mutate(split.publishable)}
                  onClear={() => setSelection(clearSelection())}
                />
              </TableSelectionBar>
            )
          }
        />
      )}

      {list.isFetchingNextPage ? (
        <p className="pp-products__more">Loading more…</p>
      ) : null}
    </div>
  );
}

/**
 * The bulk bar's split, restated **before** the action runs.
 *
 * `components.md`: *"Bulk actions restate the split before running."* Three numbers, and
 * the gap between the first and the rest is shown rather than averaged away: a filter
 * selection can name nine hundred rows while this screen has a hundred in memory, and
 * there is no bulk endpoint behind the button — §5 specifies `PATCH /products:batch (≤500)`
 * and nothing has built it. Claiming the button acts on the selection would be the same
 * class of lie as counting the selection from the loaded window.
 */
function BulkActions({
  selectedCount,
  split,
  running,
  onPublish,
  onClear,
}: {
  selectedCount: number | null;
  split: { publishable: readonly string[]; alreadyPublished: number; ineligible: number };
  running: boolean;
  onPublish: () => void;
  onClear: () => void;
}): ReactNode {
  const loadedTotal =
    split.publishable.length + split.alreadyPublished + split.ineligible;
  const beyond = selectedCount === null ? 0 : Math.max(0, selectedCount - loadedTotal);

  return (
    <>
      <span className="pp-products__split">
        {split.publishable.length} to publish
        {split.alreadyPublished > 0
          ? ` · ${split.alreadyPublished} already published`
          : ""}
        {split.ineligible > 0 ? ` · ${split.ineligible} cannot be` : ""}
        {beyond > 0 ? ` · ${beyond} not loaded on this screen yet` : ""}
      </span>
      <Button
        variant="primary"
        loading={running}
        disabled={split.publishable.length === 0}
        onClick={onPublish}
      >
        {running ? "Publishing…" : `Publish ${split.publishable.length}`}
      </Button>
      <Button variant="quiet" onClick={onClear}>
        Clear selection
      </Button>
    </>
  );
}

/**
 * Nothing matches the search — and the screen says what probably happened.
 *
 * The guess is `search-cause.ts`'s, which is a function precisely so that which guess a
 * query earns can be checked without a browser. Every branch of it renders a confident
 * sentence, so a wrong guess does not look like a bug — it looks like advice.
 */
function ZeroResults({
  query,
  status,
}: {
  query: string;
  status: ProductStatus | "";
}): ReactNode {
  if (query.trim() === "" && status !== "") {
    return (
      <Surface>
        <div className="pp-products__empty">
          <div className="pp-products__empty-title">No products match that status</div>
          <p>Nothing in this catalogue is marked “{STATUS_LABEL[status]}”.</p>
          <p className="pp-products__empty-note">
            <Link to="/products" className="pp-button" data-variant="secondary">
              Show every status
            </Link>
          </p>
        </div>
      </Surface>
    );
  }
  const cause = zeroResultCause(query);
  return (
    <Surface>
      <div className="pp-products__empty">
        <div className="pp-products__empty-title">{cause.title}</div>
        <p>{cause.body}</p>
      </div>
    </Surface>
  );
}

/** An empty catalogue. `screens.md` §3: the import door leads, add-a-product follows. */
function EmptyCatalogue(): ReactNode {
  return (
    <Surface>
      <div className="pp-products__empty">
        <div className="pp-products__empty-title">No products yet</div>
        <p>
          A catalogue in a spreadsheet takes one upload. We check every GTIN, show you the
          rows we cannot accept, and create the rest as drafts — nothing answers a scan
          until you publish it.
        </p>
        <p className="pp-products__empty-note">
          <Link to="/imports/new" className="pp-button" data-variant="primary">
            Import a spreadsheet
          </Link>{" "}
          <Link to="/products/new" className="pp-button" data-variant="secondary">
            Add one product
          </Link>
        </p>
      </div>
    </Surface>
  );
}
